import { debounce, shouldCloseOnFocusOut, throttle } from '../utils';

export default (livewireComponent = null, options = {}) => ({
  // Data properties
  itemSelected: livewireComponent,
  isMenuOpen: false,
  bgColor: 'bg-' + (options.highlightColor || 'brand-tertiary-300'),
  highlightColor: options.highlightColor || 'brand-tertiary-300',
  disabled: options.disabled || false,
  actAsButton: options.actAsButton || false,
  focusOnMount: options.focusOnMount || false,
  menuWidthAuto: options.menuWidthAuto || false,
  displayLabel: '',
  displayLabelDefault: options.displayLabelDefault || '',
  focusedOptionIndex: -1,
  itemHeight: options.itemHeight || 36,
  renderCount: 20,
  startIdx: 0,
  scrollTop: 0,
  searchInput: '',
  noResults: false,
  name: options.name || '',
  isFixedPosition: false,
  searchable: options.searchable !== false,
  scrollableContainers: options.scrollableContainers || '',
  lazyLoad: options.lazyLoad || false,
  widthMenuFixed: false,
  loading: false,

  // Server-side pagination properties
  wireMethod: options.wireMethod || '',
  // Extra positional args appended to every wireMethod call (after search, page) —
  // lets one Livewire method serve many pickers (e.g. one per dashboard tab).
  // Mirrors select-multi-server.js.
  extraArgs: Array.isArray(options.extraArgs) ? options.extraArgs : [],
  currentPage: 1,
  serverOptions: [],
  hasMore: false,
  totalCount: 0,
  includeAllOption: options.includeAllOption || false,
  allOptionLabel: options.allOptionLabel || 'All',

  get visibleOptions() {
    return this.serverOptions.slice(this.startIdx, this.startIdx + this.renderCount);
  },
  get totalHeight() {
    return this.serverOptions.length * this.itemHeight;
  },
  get offsetY() {
    return this.startIdx * this.itemHeight;
  },
  get remainingCount() {
    return Math.max(0, this.totalCount - this.serverOptions.length + (this.includeAllOption ? 1 : 0));
  },

  // Loose equality check to handle int/string type mismatches (e.g. 1 vs "1")
  valuesMatch(a, b) {
    // eslint-disable-next-line eqeqeq
    return a == b && (a !== '' && a !== null && a !== undefined || a === '' && b === '');
  },

  async fetchOptions(search = '', page = 1, append = false) {
    if (!this.wireMethod) return;
    this.loading = true;
    try {
      const result = await this.$wire[this.wireMethod](search, page, ...this.extraArgs);
      if (!result || !result.options) return;

      let newOptions = result.options;

      if (append) {
        this.serverOptions = [...this.serverOptions, ...newOptions];
      } else {
        // Prepend "All" option on first page when configured
        if (this.includeAllOption && page === 1) {
          newOptions = [{ value: '', label: this.allOptionLabel }, ...newOptions];
        }
        this.serverOptions = newOptions;
        this.scrollTop = 0;
        this.startIdx = 0;
        if (this.$refs.menu) this.$refs.menu.scrollTop = 0;
      }

      this.hasMore = result.hasMore || false;
      this.totalCount = result.total || 0;
      this.currentPage = page;
      this.noResults = this.serverOptions.length === 0 ||
        (this.serverOptions.length === 1 && this.includeAllOption && !search);

      // Resolve display label for pre-selected value after options load
      if (this.itemSelected != null && this.itemSelected !== '' && !this.actAsButton) {
        const found = this.serverOptions.find(o => this.valuesMatch(o.value, this.itemSelected));
        if (found) {
          this.displayLabel = found.label;
        }
      }
    } catch {
      // fetch error handled silently
    } finally {
      this.loading = false;
    }
  },

  async loadMore() {
    if (!this.hasMore || this.loading) return;
    await this.fetchOptions(this.searchInput, this.currentPage + 1, true);
  },

  handleScroll(e) {
    this.scrollTop = e.target.scrollTop;
    const maxStartIdx = Math.max(0, this.serverOptions.length - this.renderCount);
    this.startIdx = Math.min(Math.floor(this.scrollTop / this.itemHeight), maxStartIdx);
  },

  // Utility methods
  getBackgroundColor() {
    let bgcolor = options.classString || '';
    const regex = /\bbg-[\w-]+/g;
    const match = bgcolor.match(regex);
    return match ? match[0] : '';
  },

  focusInput() {
    const input = this.$refs.input;
    if (input) input.focus();
  },

  scrollToFocused() {
    if (this.focusedOptionIndex === -1) return;
    const itemTop = this.focusedOptionIndex * this.itemHeight;
    const itemBottom = itemTop + this.itemHeight;
    const menu = this.$refs.menu;
    if (!menu) return;
    const menuHeight = menu.clientHeight;

    if (itemTop < this.scrollTop) {
      menu.scrollTop = itemTop;
      this.scrollTop = itemTop;
    } else if (itemBottom > this.scrollTop + menuHeight) {
      menu.scrollTop = itemBottom - menuHeight;
      this.scrollTop = menu.scrollTop;
    }
    this.startIdx = Math.floor(this.scrollTop / this.itemHeight);
  },

  activeSelected() {
    const idx = this.serverOptions.findIndex(o => this.valuesMatch(o.value, this.itemSelected));
    if (idx !== -1) {
      this.focusedOptionIndex = idx;
    } else if (this.serverOptions.length > 0) {
      this.focusedOptionIndex = 0;
    } else {
      this.focusedOptionIndex = -1;
    }
    this.$nextTick(() => this.scrollToFocused());
  },

  maybeFixPosition() {
    if (!this.scrollableContainers) return;
    const popupContainer = this.$refs.popupContainer;
    const sortBtn = this.$refs.sortBtn;
    const sortBtnRect = sortBtn.getBoundingClientRect();

    const viewportWidth = window.innerWidth - 16;
    const viewportHeight = window.innerHeight - 16;

    popupContainer.style.visibility = 'hidden';
    popupContainer.style.display = 'block';
    popupContainer.style.position = 'fixed';
    this.isFixedPosition = true;

    const popupContainerHeight = popupContainer.offsetHeight;
    popupContainer.style.width = '1px';

    let top = sortBtnRect.top + sortBtnRect.height;
    let left = sortBtnRect.left;

    if (top + popupContainerHeight > viewportHeight) {
      top = sortBtnRect.top - popupContainerHeight;
      this.$refs.menu.classList.add('h-60');
    }

    if (left + popupContainer.offsetWidth > viewportWidth) {
      left = viewportWidth - popupContainer.offsetWidth;
    }

    popupContainer.style.top = `${Math.max(0, top + 5)}px`;
    popupContainer.style.left = `${Math.max(0, left)}px`;

    popupContainer.style.removeProperty('visibility');
    popupContainer.style.removeProperty('display');
  },

  onMenuOpen() {
    this.maybeFixPosition();
    this.attachScrollEventListeners();

    // Always reset search and fetch fresh data from server
    this.searchInput = '';
    this.fetchOptions('', 1).then(() => {
      if (!this.isMenuOpen) return;
      this.activeSelected();
    });

    setTimeout(() => {
      if (!this.isMenuOpen) return;
      this.focusInput();
    }, 50);

    setTimeout(() => {
      if (!this.isMenuOpen) return;
      this.fixMenuWidth();
    }, 120);
  },

  attachScrollEventListeners() {
    if (!this.scrollableContainers) return;
    const onScrollAdjustPosition = throttle((e) => {
      if (!this.isMenuOpen) {
        e.target.removeEventListener('scroll', onScrollAdjustPosition);
        return;
      }

      if (!this.isElementInvisibleInContainer(this.$refs.sortBtn, e.target)) {
        this.closeAndRestoreFocus();
        e.target.removeEventListener('scroll', onScrollAdjustPosition);
        return;
      }

      this.maybeFixPosition();
    }, 10);

    const elements = document.querySelectorAll(this.scrollableContainers);
    elements.forEach(el => {
      el.addEventListener('scroll', onScrollAdjustPosition);
    });
  },

  isElementInvisibleInContainer(el, container) {
    const elRect = el.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    return (
      elRect.top >= containerRect.top &&
      elRect.bottom <= containerRect.bottom &&
      elRect.left >= containerRect.left &&
      elRect.right <= containerRect.right
    );
  },

  handleKeydown() {
    if (this.isMenuOpen) return;
    this.isMenuOpen = true;
    this.$nextTick(() => {
      this.onMenuOpen();
    });
  },

  handleFocusOut(event) {
    if (!this.isMenuOpen) return;
    // $refs.sortBtn IS the element a <label for> resolves to, so its live id is the one to match.
    if (shouldCloseOnFocusOut(event, {
      triggerId: this.$refs.sortBtn?.id || options.id,
      componentEl: this.$el,
      menuEl: this.$refs.popupContainer,
    })) {
      this.isMenuOpen = false;
    }
  },

  handleBtnClick(e) {
    if (this.isMenuOpen) {
      this.closeAndRestoreFocus();
    } else {
      this.isMenuOpen = true;
      this.$nextTick(() => {
        this.onMenuOpen();
      });
    }
  },

  fixMenuWidth() {
    if (this.widthMenuFixed) return;
    let popupContainer = this.$refs.popupContainer;
    if (!popupContainer) return;
    const width = popupContainer.getBoundingClientRect().width;
    if (width === 0) return;
    popupContainer.style.width = width + 'px';
    popupContainer.style.minWidth = width + 'px';
    popupContainer.style.maxWidth = width + 'px';
    this.widthMenuFixed = true;
  },

  handleEscapeKeyPress() {
    if (!this.isMenuOpen) return;
    if (this.searchInput) {
      return;
    }
    this.closeAndRestoreFocus();
  },

  closeAndRestoreFocus() {
    this.isMenuOpen = false;
    this.searchInput = '';
    this.restoreFocus();
    this.widthMenuFixed = false;
    if (this.$refs.popupContainer) {
      this.$refs.popupContainer.style.width = '';
      this.$refs.popupContainer.style.minWidth = '';
    }
  },

  restoreFocus() {
    this.$nextTick(() => {
      this.$refs.sortBtn.focus();
    });
  },

  moveActiveDown() {
    if (this.noResults) return;
    if (this.focusedOptionIndex >= this.serverOptions.length - 1) {
      this.focusedOptionIndex = 0;
    } else {
      this.focusedOptionIndex++;
    }
    this.scrollToFocused();
  },

  moveActiveUp() {
    if (this.noResults) return;
    if (this.focusedOptionIndex <= 0) {
      this.focusedOptionIndex = this.serverOptions.length - 1;
    } else {
      this.focusedOptionIndex--;
    }
    this.scrollToFocused();
  },

  handleKeyEnter() {
    if (this.noResults || this.focusedOptionIndex === -1) return;
    const option = this.serverOptions[this.focusedOptionIndex];
    if (option) {
      this.handleItemClicked(option.value, option.label);
    }
  },

  handleItemClicked(value, label) {
    if (!this.valuesMatch(this.itemSelected, value)) {
      this.itemSelected = value;
      if (!this.actAsButton) {
        this.displayLabel = label;
      }
      this.$dispatch(`${options.id}_item_selected_select2`, { selected: this.itemSelected, label });
    }
    this.closeAndRestoreFocus();
  },

  // Initialize
  init() {
    // Fetch initial page. lazyLoad skips it: onMenuOpen() always refetches from
    // page 1 anyway, so for a picker that mounts in bulk (one per row of a
    // modal) the eager fetch is N discarded server round trips per open.
    if (!this.lazyLoad) {
      this.fetchOptions('', 1);
    }

    // Watch itemSelected changes
    this.$watch('itemSelected', (newValue, oldValue) => {
      if (newValue != oldValue && !this.actAsButton) {
        // Resolve the option first so an includeAllOption entry (value '') keeps its
        // own label; only an unmatched clear (an entangled model reset after a
        // successful submit) falls back to the default instead of leaving the
        // previous pick's label on the trigger.
        const found = this.serverOptions.find(o => this.valuesMatch(o.value, newValue));
        if (found) {
          this.displayLabel = found.label;
        } else if (newValue === '' || newValue === null) {
          this.displayLabel = this.displayLabelDefault;
        }
      }
    });

    // Watch searchInput changes — fetch from server (only when menu is open)
    this.$watch('searchInput', debounce(value => {
      if (!this.isMenuOpen) return;
      this.fetchOptions(value, 1);
    }, 300));

    // Set initial display label
    if (this.displayLabelDefault) {
      this.displayLabel = this.displayLabelDefault;
    }

    // Focus on mount if needed
    if (this.focusOnMount) {
      this.restoreFocus();
    }
  },

  destroy() {}
});
